import {
  BaseUserProfile, bodyComposition, Equipment,
  Gender, Guardian, HandledError, IAccountComment,
  IBodyAssessment, IDemerit, IEmailsSent,
  IUserLeaderboardPreferences, IWeeklyWorkoutMinutesTarget,
  IWorkoutImportConfig,
  KnownMediums, LegalAgreement,
  Level, MealPreferences, NutritionRating, ProgramType,
  UserNotification,
  WorkoutImportOrigin,
} from 'fitbeat.models';
import moment from 'moment';
import { IDisplayableUserProfile } from './idisplayableuserprofile';

export class UserProfile extends BaseUserProfile {
  public static mapUserUpdateableFields(newUserProfile: UserProfile) {
    return {
      email: newUserProfile.email,
      nickname: newUserProfile.nickname,
      username: newUserProfile.username,
      avatarName: newUserProfile.avatarName,
      weight: newUserProfile.weight,
      height: newUserProfile.height,
      age: newUserProfile.age,
      addressLine1: newUserProfile.addressLine1,
      addressLine2: newUserProfile.addressLine2,
      mobileNumber: newUserProfile.mobileNumber,
      gender: newUserProfile.gender,
      dateOfBirth: newUserProfile.dateOfBirth,
      notifications: newUserProfile.notifications,
      legalAgreements: newUserProfile.legalAgreements,
      heartRateMonitorIdentifier: newUserProfile.heartRateMonitorIdentifier,
      hasUsedExtendedFreeTrail: newUserProfile.hasUsedExtendedFreeTrail,
      postCode: newUserProfile.postCode,
      suburb: newUserProfile.suburb,
      unit: newUserProfile.unit,
      country: newUserProfile.country,
      state: newUserProfile.state,
      pushMessageSettings: newUserProfile.pushMessageSettings,
      firstname: newUserProfile.firstname,
      lastname: newUserProfile.lastname,
      guardianInfo: newUserProfile.guardianInfo,
      branch: newUserProfile.branch,
      fullname: BaseUserProfile.buildFullname(newUserProfile),
      longTermGoal: newUserProfile.longTermGoal,
      heartRateMax: newUserProfile.heartRateMax,
      skillLevel: newUserProfile.skillLevel,
      shouldShowWelcomeVideo: newUserProfile.shouldShowWelcomeVideo,
      shouldShowTrackingOnboarding: newUserProfile.shouldShowTrackingOnboarding,
      useImperialUnit: newUserProfile.useImperialUnit,
      mealPreferences: newUserProfile.mealPreferences,
      weeklyWorkoutMinutesTarget: newUserProfile.weeklyWorkoutMinutesTarget,
      defaultEquipment: newUserProfile.defaultEquipment,
      specialOffers: newUserProfile.specialOffers,
      strengthRatioForMuscleGroups: newUserProfile.strengthRatioForMuscleGroups,
      allergens: newUserProfile.allergens,
      isHighlyActive: newUserProfile.isHighlyActive,
      teams: newUserProfile.teams,
    };
  }

  public static addMedicalAgreements(userProfile: UserProfile): UserProfile {
    const medicalAgreement = BaseUserProfile.createMedicalAgreement();
    const isMinor = BaseUserProfile.isMinor(userProfile.dateOfBirth);
    if (isMinor) {
      const { guardianInfo } = userProfile;
      if (guardianInfo && guardianInfo.legalAgreements) {
        userProfile.guardianInfo.legalAgreements.push(medicalAgreement);
      } else {
        userProfile.guardianInfo = new Guardian();
        userProfile.guardianInfo.legalAgreements = [medicalAgreement];
      }
    }

    if (userProfile.legalAgreements) {
      userProfile.legalAgreements.push(medicalAgreement);
    } else {
      userProfile.legalAgreements = [medicalAgreement];
    }

    return userProfile;
  }

  public static hasRequiredSignupDetails(userProfile: UserProfile): boolean {
    return !!(userProfile.firstname &&
      userProfile.lastname &&
      userProfile.gender &&
      userProfile.weight &&
      userProfile.nickname &&
      userProfile.height);
  }

  public static hasCompletedDetailsNeededForHomeUserProfile(userProfile: UserProfile): boolean {
    return userProfile.legalAgreements &&
      userProfile.legalAgreements.length > 0 &&
      !!userProfile.firstname &&
      !!userProfile.lastname;
  }

  public static hasRequiredStripeBillingDetails(userProfile: UserProfile): boolean {
    return !!(userProfile.addressLine1);
  }

  public static hasRequiredPeSignupDetails(userProfile: UserProfile): boolean {
    return !!(userProfile.country &&
      userProfile.dateOfBirth);
  }

  public static hasLongTermGoal(userProfile: UserProfile): boolean {
    return userProfile.longTermGoal && userProfile.longTermGoal !== ProgramType.NotSet;
  }

  public static hasSkillLevel(userProfile: UserProfile): boolean {
    return !!userProfile.skillLevel;
  }

  public static isUserPtApproved(userProfile: UserProfile): boolean {
    return !!userProfile.ptApprovedAt;
  }

  public static hasAddressDetails(userProfile: UserProfile): boolean {
    return !!(userProfile.addressLine1 &&
      userProfile.suburb &&
      userProfile.postCode &&
      userProfile.country &&
      userProfile.state
    );
  }

  public static hasMetricInfoForHRMonitor(userProfile: UserProfile): boolean {
    return !!(userProfile.dateOfBirth &&
      userProfile.gender &&
      userProfile.weight &&
      userProfile.height);
  }

  public static hasBodyAssessment(userProfile: UserProfile): boolean {
    return !!userProfile.bodyAssessment;
  }

  public static hasBodyComposition(userProfile: UserProfile): boolean {
    return !!userProfile.bodyComposition;
  }

  public static hasSmartScale(userProfile: UserProfile): boolean {
    return !!userProfile.bodyAssessment;
  }

  public static hasValidEmail(email: string): boolean {
    if (!email) {
      throw new HandledError(`The Email field is required.`);
    }
    const emailRegex = /\S+@\S+\.\S+/;
    const regexp = new RegExp(emailRegex);
    if (!regexp.test(email)) {
      throw new HandledError(`'${email}' is not a valid email`);
    }
    return true;
  }

  public static hasValidSkillLevel(skillLevel: string): boolean {
    if (!skillLevel) {
      throw new HandledError(`The Skill Level field is required.`);
    }
    if (!(Object as any).values(Level).includes(skillLevel)) {
      throw new HandledError(`'${skillLevel}' is not a valid skill level`);
    }
    return true;
  }

  public static hasValidLongTermGoal(longTermGoal: string): boolean {
    if (!longTermGoal) {
      throw new HandledError(`The Fitness Goal field is required.`);
    }
    if (!(Object as any).values(ProgramType).includes(longTermGoal)) {
      throw new HandledError(`'${longTermGoal}' is not a valid goal`);
    }
    return true;
  }

  public static hasValidDateOfBirth(dateOfBirth: string): boolean {
    if (!dateOfBirth) {
      throw new HandledError(`The Date of Birth field is required.`);
    }
    if (!moment(dateOfBirth, 'YYYY-MM-DD', true).isValid()) {
      throw new HandledError(`'${dateOfBirth}' is not a valid date of birth`);
    }
    return true;
  }

  public static hasValidGender(gender: string): boolean {
    if (!gender) {
      throw new HandledError(`The Gender field is required.`);
    }
    if (!(Object as any).values(Gender).includes(gender)) {
      throw new HandledError(`'${gender}' is not a valid gender`);
    }
    return true;
  }

  public static hasValidHeartRateMonitor(heartRateMonitorIdentifier: string): boolean {
    const parsed = parseInt(heartRateMonitorIdentifier, 10);
    if (heartRateMonitorIdentifier && !parsed || parsed === 0) {
      throw new HandledError(`'${heartRateMonitorIdentifier}' is not a valid heart rate monitor`);
    }
    return true;
  }

  public static hasValidPhoneNumber(mobileNumber: string) {
    if (!mobileNumber) {
      throw new HandledError(`The Mobile Number field is required.`);
    }
    const mobileRegex = /^\+?[0-9]{7,15}$/;
    const regexp = new RegExp(mobileRegex);
    if (!regexp.test(mobileNumber)) {
      throw new HandledError(`'${mobileNumber}' is not a valid phone number`);
    }
    return true;
  }

  public static checkMaxLength(fieldDict: any, max: number): boolean {
    const fieldName = Object.keys(fieldDict)[0];
    const fieldValue = fieldDict[fieldName];

    if (fieldValue && fieldValue.length >= max) {
      const lookup = {
        addressLine1: 'Address line 1',
        addressLine2: 'Address line 2',
        postCode: 'Post Code',
        suburb: 'Suburb',
        unit: 'Unit',
        state: 'State',
        nickname: 'Nickname',
        firstname: 'First Name',
        lastname: 'Last Name',
      };
      let msg = `The field exceeded ${max} characters.`;
      if (typeof lookup[fieldName] !== 'undefined') {
        msg = `The ${lookup[fieldName]} field exceeded ${max} characters.`;
      }
      throw new HandledError(msg);
    }
    return true;
  }

  public static checkNumberRange(fieldDict: any, min: number, max: number): boolean {
    const fieldName = Object.keys(fieldDict)[0];
    const fieldValue = fieldDict[fieldName];

    if (fieldValue && isNaN(fieldValue)) {
      throw new HandledError(`The ${fieldName} field must be a number.`);
    }

    if (fieldValue && !(fieldValue >= min && fieldValue <= max)) {
      throw new HandledError(`The ${fieldName} field must be between ${min} and ${max}`);
    }
    return true;
  }

  public static isRequired(fieldDict: any) {
    const fieldName = Object.keys(fieldDict)[0];
    const fieldValue = fieldDict[fieldName];

    if (!fieldValue) {
      throw new HandledError(`The ${fieldName} field is required.`);
    }
    return true;
  }

  public static signedUpThroughInfluencerLink(userProfile: UserProfile) {
    return userProfile.signupSource?.medium === KnownMediums.influencer;
  }

  public static createUserProfileFromDbObject(user: Partial<UserProfile>): UserProfile {
    if (user.demerits) {
        user.demerits = user.demerits.map((d: IDemerit) => {
            d.date = new Date(d.date);
            return d;
        });
    }
    const userProfile = new UserProfile(user.uid, user);
    userProfile.setAgeFromDateOfBirth();
    return userProfile;
}

public static hasValidInfluencerSettings(discount: number, source: string) {
  if(!source) {
    throw new HandledError('Source is required');
  }
  if(!discount) {
    throw new HandledError('Discount is required');
  }
  if(typeof discount === 'number') {
     throw new HandledError('Discount must be a number');
  }
  if(discount <0 || discount > 100) {
    throw new HandledError('Discount must be a number between 1 and 100');
  }

  return true;
}

public static hasValidWorkoutImportConfig(userProfile: UserProfile, originToCheck: WorkoutImportOrigin) {
  if (userProfile.WorkoutImportConfig) {
    const userHasConfig = userProfile.WorkoutImportConfig.some(
      (WIC: IWorkoutImportConfig) => (WIC.origin === originToCheck),
    );
    if (userHasConfig) {
      return true;
    }
  }
  return false;
}

public static removeWorkoutImportConfig(userProfile: UserProfile, originToRemove: WorkoutImportOrigin):
  UserProfile {
  const newWorkoutImportConfig = userProfile.WorkoutImportConfig?.filter((WIC) =>
    WIC.origin !== originToRemove);
  userProfile.WorkoutImportConfig = newWorkoutImportConfig;
  return userProfile;
}

  // user updateable fields
  public readonly nickname: string;
  public username: string;
  public readonly avatarName: string;
  public weight: number;
  public height: number;
  public readonly age: number;
  public dateOfBirth: string; // "YYYY-MM-DD"
  public readonly addressLine1: string;
  public readonly addressLine2: string;
  public readonly postCode: string;
  public readonly suburb: string;
  public readonly unit: string;
  public country: string;
  public readonly state: string;
  public mobileNumber: string;
  public notifications: UserNotification[];
  public legalAgreements: LegalAgreement[];
  /**
   * @deprecated Use heartRateMonitors
   */
  public heartRateMonitorIdentifier: string;
  public pushMessageSettings: any;
  public guardianInfo: Guardian;
  public shouldShowWelcomeVideo: boolean = false;
  public shouldShowTrackingOnboarding: boolean = false;
  public homeWorkoutInstructor?: string;
  public nutritionRatings: NutritionRating[];
  public bodyAssessment?: IBodyAssessment;
  public bodyComposition?: bodyComposition;
  public defaultEquipment?: Equipment[];
  // only updated in backend
  public userSignupDate: string;
  public avatarPicture: string;
  public emailsSent: IEmailsSent;
  public demerits: IDemerit[];
  public partnerWhitelist: IDisplayableUserProfile[];
  public partnerBlacklist: IDisplayableUserProfile[];
  public privateProfile: boolean = false;
  public mealPreferences: MealPreferences;
  public ptApprovedAt: number;
  public invitationCode: string;
  public has24HourAccess: boolean;
  public accountComments?: IAccountComment[];
  public followUpDate?: Date;
  public weeklyWorkoutMinutesTarget: IWeeklyWorkoutMinutesTarget[];
  public leaderboardPreferences?: IUserLeaderboardPreferences;

  constructor(uid?: string, initData?: Partial<UserProfile>) {
    super(uid, initData);
    if (!initData) {
      return;
    }
    Object.assign(this, initData);
    this.setAgeFromDateOfBirth();
    this.setHeartRateMax();
  }

  public isCurrentPlanActiveAt = (timestamp: number): boolean => {
    const startOfDay = this.getStartOfDayFor(timestamp);
    const expiresAfterStartOfToday = (!!this.currentPlanExpiresAt && this.currentPlanExpiresAt >= startOfDay);
    return BaseUserProfile.hasActivePlan(this)
      && (!this.currentPlanCancelAtEndOfTerm || expiresAfterStartOfToday);
  }

  private getStartOfDayFor = (timestamp: number): number => {
    const time = new Date(timestamp);
    time.setHours(0);
    time.setMinutes(0);
    time.setMilliseconds(0);
    time.setSeconds(0);
    return time.getTime();
  }
}
